import React, { useEffect, useRef } from 'react';

import { Navigate } from 'react-router-dom';

import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../hooks/useAuth';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading, loginWithRedirect } = useAuth();
  const { selectedTenant, isLoading: tenantLoading, availableTenants } = useTenant();

  // Track if login has been triggered to prevent multiple redirects
  const loginTriggered = useRef(false);

  // Trigger login redirect if not authenticated and not loading
  useEffect(() => {
    if (!authLoading && !isAuthenticated && !loginTriggered.current) {
      loginTriggered.current = true;
      loginWithRedirect();
    }
  }, [authLoading, isAuthenticated, loginWithRedirect]);

  // Reset the flag when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loginTriggered.current = false;
    }
  }, [isAuthenticated]);

  // Show loading while checking auth status
  if (authLoading) {
    return <LoadingOverlay />;
  }

  // If not authenticated, show loading while redirecting to login
  if (!isAuthenticated) {
    return <LoadingOverlay />;
  }

  // Show loading while tenant data is being fetched
  if (tenantLoading) {
    return <LoadingOverlay />;
  }

  // If user has no tenants, redirect to no-access page
  if (availableTenants.length === 0) {
    return <Navigate to="/no-access" replace />;
  }

  // If user has tenants but none selected, redirect to tenant selection
  if (availableTenants.length > 0 && !selectedTenant) {
    return <Navigate to="/select-tenant" replace />;
  }

  // All checks passed, render the protected content
  return <React.Fragment>{children}</React.Fragment>;
};
